/**
 * The page is imported from **its own module**, not from the `@pages` barrel — the barrel pulls
 * every screen into one shared chunk and defeats the route-level code splitting (EPIC-003).
 */
import { createFileRoute } from '@tanstack/react-router';

import { AdminAiProvidersPage } from '@pages/admin-ai-providers';
import { AiProviderModel } from '@units/ai-provider';
import { IamService } from '@units/iam';

/**
 * `/admin/ai-providers` — wiring only (`rules/frontend-fsd.mdc` rule 10).
 *
 * The guard is `ai_provider:read`, which is what `GET /ai/providers` declares. Writing is a second
 * permission and is checked where the actions are, on the screen: a reader may look at what is
 * configured and at what it costs without being offered a form they would be refused.
 *
 * `whenDenied: 'forbidden'` rather than a 404, for the reason the rest of `/admin/**` gives: the
 * section is in everybody's navigation, so its existence is no secret and the refusal may name what
 * is missing (`ux-architecture.md` → «403 vs 404»).
 *
 * **No loader**, so the route inherits the router's pending and error boundaries rather than
 * declaring its own. The screen reads two independent queries and renders each one's states
 * separately — a summary that failed must not replace a provider list that loaded.
 */
export const Route = createFileRoute('/_authenticated/admin/ai-providers')({
  beforeLoad: IamService.IamGuards.requirePermission({
    permission: 'ai_provider:read',
    whenDenied: 'forbidden',
  }),
  validateSearch: AiProviderModel.aiProviderSearchSchema,
  component: AdminAiProvidersPage,
  staticData: { crumbKey: 'ai.title' },
});
